import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { DashboardSummariesResponse } from '@shared/api';
import { Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface PaymentItemProps {
  className?: string;
  mainAccount: DashboardSummariesResponse;
  subAccounts: DashboardSummariesResponse[];
}

export const PaymentItem = (props: PaymentItemProps) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const { t } = useTranslation('dashboard');

  return (
    <div
      className={cn(
        'flex min-h-32 bg-white dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && 'border-b border-gray-black pb-6 last:border-none',
        props.className,
      )}
    >
      {PaletteUtils.isInsigniaPalette && (
        <div className={cn('w-16 min-w-16 bg-gradient-to-b from-black to-yellow-ocher', 'max-md:w-2 max-md:min-w-2')} />
      )}
      <div className={cn('flex-1', PaletteUtils.isInsigniaPalette && ['px-3 py-3.5', 'max-md:px-2'])}>
        <div className='gap-3'>
          {PaletteUtils.isVcfcPalette && (
            <>
              <Text className='mb-1 text-dark-wet-asphalt/60 dark:text-white/50' size='xxs'>
                {t('payment.account.titleVcfc')}
              </Text>
              <Text className='max-md:text-base' size='xl'>
                {props.mainAccount.iban}
              </Text>
            </>
          )}
          {PaletteUtils.isInsigniaPalette && (
            <div className={cn('flex justify-between gap-3', 'max-md:flex-col max-md:gap-2')}>
              <div className={cn('flex flex-1 gap-3', 'max-md:flex-col max-md:gap-2')}>
                <div className={cn('mt-3 border-r border-light pr-3', 'max-md:mt-0 max-md:border-b max-md:border-r-0 max-md:pb-2 max-md:pr-0')}>
                  <Text weight='semibold' truncate>
                    {props.mainAccount.accountName || FormatUtils.formatAccountNumber(props.mainAccount.accountNumber)}
                  </Text>
                  <Text uppercase weight='semibold' size='xs' color='gray' truncate>
                    {props.mainAccount.productName}
                  </Text>
                </div>
                <div className={cn('mt-3', 'max-md:mt-0')}>
                  <Text className='break-all' weight='semibold' size='base'>
                    {props.mainAccount.iban}
                  </Text>
                  <Text uppercase weight='semibold' size='3xs' color='gray'>
                    {t('payment.account.fields.iban')}
                  </Text>
                </div>
              </div>
              <div className={cn('flex shrink-0 items-center gap-1', 'max-md:justify-between')}>
                <div className='flex flex-wrap gap-1'>
                  <div className='flex h-8 items-center rounded border px-2.5 text-base font-semibold text-light-gray'>
                    {CurrencyUtils.getSymbolByCode(props.mainAccount.currency)}
                  </div>
                  {props.subAccounts?.length > 0 &&
                    props.subAccounts.map((account) => (
                      <div key={account.id} className='flex h-8 items-center rounded border px-2.5 text-base font-semibold text-light-gray'>
                        {CurrencyUtils.getSymbolByCode(account.currency)}
                      </div>
                    ))}
                </div>
                <div className='mx-2 flex cursor-pointer items-center' onClick={() => setIsCollapsed((prev) => !prev)}>
                  <div className={cn('-rotate-90 transition-transform duration-200', isCollapsed && 'rotate-90')}>
                    <Icon icon={<ChevronIcon />} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {PaletteUtils.isInsigniaPalette && <hr className='mx-0 my-3 text-light' />}
        {!isCollapsed && (
          <div className={cn('flex flex-col', PaletteUtils.isInsigniaPalette && 'gap-3', PaletteUtils.isVcfcPalette && 'mt-4 gap-4')}>
            <Item account={props.mainAccount} />
            {props.subAccounts.map((account) => (
              <Item key={account.id} account={account} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ItemProps {
  className?: string;
  account: DashboardSummariesResponse;
}

const Item = (props: ItemProps) => {
  const { t } = useTranslation('dashboard');

  return (
    <div
      className={cn(
        'flex gap-6',
        PaletteUtils.isInsigniaPalette && [
          'bg-ice px-3 py-2 dark:bg-slate',
          'max-md:flex-col max-md:items-start max-md:gap-2 max-md:px-2',
          'max-lg:flex-col max-lg:gap-2',
        ],
        PaletteUtils.isVcfcPalette && [
          'h-[52px] items-center justify-between rounded-lg bg-ghost-white px-4 dark:bg-light/5',
          'max-md:h-full max-md:flex-col max-md:items-start max-md:gap-3 max-md:py-2',
        ],
        props.className,
      )}
    >
      <div
        className={cn(
          'flex h-full items-center justify-center rounded border p-0.5 px-2 text-center font-semibold text-primary',
          PaletteUtils.isInsigniaPalette && 'w-fit shrink-0 gap-2',
          PaletteUtils.isVcfcPalette && ['border-none dark:text-white', 'max-md:p-0'],
        )}
      >
        {PaletteUtils.isInsigniaPalette && (
          <Text weight='semibold' color='primary-only'>
            {CurrencyUtils.getSymbolByCode(props.account.currency)}
          </Text>
        )}
        <Text
          className={cn(PaletteUtils.isInsigniaPalette && 'mt-0.5')}
          weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined}
          size='xs'
          color={PaletteUtils.isVcfcPalette ? 'primary' : 'primary-only'}
          uppercase={PaletteUtils.isInsigniaPalette}
        >
          {props.account.mainAccId ? t('payment.account.accountType.sub') : t('payment.account.accountType.main')}
        </Text>
        {PaletteUtils.isVcfcPalette && (
          <div className='flex items-center'>
            <div className='mx-1 h-1 w-1 rounded-full bg-ash-blue' />
            <Text weight='medium' size='xs' uppercase>
              {AmountUtils.transformToAmountWithCurrency(props.account.availableBalance, props.account.currency)}
            </Text>
          </div>
        )}
      </div>
      {PaletteUtils.isInsigniaPalette && (
        <div>
          <Text tag='h3' size='xs' color='secondary'>
            {t('payment.account.fields.accountBalance')}
          </Text>
          <Text tag='h4' weight='semibold' size='xs'>
            {AmountUtils.transformToAmountWithCurrency(props.account.availableBalance, props.account.currency)}
          </Text>
        </div>
      )}
    </div>
  );
};
